import type { AddonManifest, Catalog } from "./index";

/** Non-catalog resource routing shared by the server and receiver clients.
 * String declarations inherit the manifest filters. Object declarations own
 * their types/prefixes; absent or empty prefixes mean all IDs, as in the pinned
 * Stremio core. An object without types does not declare a supported type.
 * Catalog resources use their own catalog/extra contract instead. */
export function addonResourceSupported(
  manifest: Pick<AddonManifest, 'resources' | 'types' | 'idPrefixes'>,
  resource: string,
  type: string,
  id: string,
): boolean {
  return manifest.resources.some(entry => {
    if ((typeof entry === 'string' ? entry : entry.name) !== resource) return false;
    const types = typeof entry === 'string' ? manifest.types : entry.types;
    const prefixes = typeof entry === 'string' ? manifest.idPrefixes : entry.idPrefixes;
    return !!types?.includes(type) && (!prefixes?.length || prefixes.some(prefix => id.startsWith(prefix)));
  });
}

/** The same declared filters drive transport validation and client controls. */
export function addonCatalogFields(catalog: Catalog): NonNullable<Catalog["extra"]> {
  return (
    catalog.extra ??
    [...new Set([...(catalog.extraSupported ?? []), ...(catalog.extraRequired ?? [])])].map(
      (name) => ({ name, isRequired: catalog.extraRequired?.includes(name) }),
    )
  );
}

export function addonExtraAllowed(
  field: NonNullable<Catalog["extra"]>[number],
  value: string | undefined,
): boolean {
  if (!value?.trim()) return !field.isRequired;
  if (!field.options) return true;
  // An option can itself contain commas; exact matches take precedence.
  const values = field.options.includes(value) ? [value] : value.split(",");
  return (
    values.length <= (field.optionsLimit ?? 1) &&
    new Set(values).size === values.length &&
    values.every((option) => field.options!.includes(option))
  );
}
